import type { IModuleDefinition, IModuleStrings } from '@platform/core/types';
import { overriddenText } from './overriddenText';
import { sameValueText } from './sameValueText';

/**
 * Рядки модуля з урахуванням перейменувань простору (ADR-023): підписи полів, варіантів, назви типу запису й модуля, які злите визначення
 * сервера змінило проти опублікованого визначення пакета (`published`), показуються замість рядків пакета i18n; решта — як у `base`.
 * Значення варіантів власних полів простору без підпису перекладаються як те саме значення в полях модуля (sameValueText).
 * Без опублікованого визначення порівнювати нема з чим — `base` без змін. Чиста функція.
 */
export function workspaceStrings(base: IModuleStrings, published: IModuleDefinition | undefined): IModuleStrings {
  if (!published) return base;
  const { lang } = base;
  const rtOf = (key: string) => published.recordTypes.find((r) => r.key === key);
  const fieldOf = (rtKey: string, fieldKey: string) => rtOf(rtKey)?.fields.find((f) => f.key === fieldKey);
  return {
    ...base,
    moduleName: (fallback) => overriddenText(fallback, published.module.name, lang) ?? base.moduleName(fallback),
    entity: (rtKey, part, fallback) => {
      const rt = rtOf(rtKey);
      return overriddenText(fallback, rt ? rt[part] ?? null : false, lang) ?? base.entity(rtKey, part, fallback);
    },
    field: (rtKey, fieldKey, fallback) => {
      const field = fieldOf(rtKey, fieldKey);
      return overriddenText(fallback, field ? field.label : false, lang) ?? base.field(rtKey, fieldKey, fallback);
    },
    option: (rtKey, fieldKey, value, fallback) => {
      const field = fieldOf(rtKey, fieldKey);
      const option = field?.options?.find((o) => o.value === value);
      const text = overriddenText(fallback, option ? option.label ?? null : false, lang) ?? base.option(rtKey, fieldKey, value, fallback);
      // власне поле простору: значення без підпису — перекладом того самого значення з полів модуля (як словник прототипу)
      return !field && text === value ? sameValueText(base, published, rtKey, value) ?? text : text;
    },
  };
}
